import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminGetModelOptions,
  adminListModelsOptions,
  adminListModelsQueryKey,
  adminListTierDefaultsOptions,
  adminListTierDefaultsQueryKey,
  listUserFacingTierDefaultsQueryKey,
  modelsListModelsQueryKey,
} from "@/api";

/** The whole catalog in one request (the endpoint's max page size), for lists and replacement pickers. */
export const ALL_MODELS = { query: { page: 1, page_size: 100 } };

export function useAdminModels() {
  return useQuery(adminListModelsOptions(ALL_MODELS));
}

/** One model (GET /admin/models/{id}), for the configure page. */
export function useAdminModel(modelId: string) {
  return useQuery(adminGetModelOptions({ path: { model_id: modelId } }));
}

export function useTierDefaults() {
  return useQuery(adminListTierDefaultsOptions());
}

const isSingleModelQuery = (queryKey: readonly unknown[]) =>
  (queryKey[0] as { _id?: string } | undefined)?._id === "adminGetModel";

/** Refresh the admin lists, every single-model query and the app-wide model pickers after any change. */
export function useRefreshModels() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      ...[
        adminListModelsQueryKey(),
        adminListTierDefaultsQueryKey(),
        modelsListModelsQueryKey(),
        listUserFacingTierDefaultsQueryKey(),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      queryClient.invalidateQueries({ predicate: (query) => isSingleModelQuery(query.queryKey) }),
    ]);
}
